import { useMemo } from 'react';

import dayjs from 'dayjs';

import { tokens } from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useWorkloadEventMetricsQuery } from '@cast/shared-cost-data-access';
import {
  useOomKillEventMetricsChartData,
  type OomKillBucketQueryRange,
  type OOMKillChartItem,
  type OOMKillTotals,
} from '@cast/split-me/features/cluster/dashboard/WorkloadEventMetricsSection';
import { SECONDS_IN_DAY, SECONDS_IN_HOUR } from '@cast/utils';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

const TOP_WORKLOADS_LIMIT = 7;

const WORKLOAD_COLORS = [
  tokens.theme.data.grey900,
  tokens.theme.data.blue300,
  tokens.theme.data.green300,
  tokens.theme.data.red500,
  tokens.theme.data.yellow200,
  tokens.theme.data.aqua400,
  tokens.theme.data.indigo400,
];

const MANAGED_COLOR = tokens.theme.data.red500;
const UNMANAGED_COLOR = tokens.theme.data.red100;

type InventoryOomKillsTopItem = {
  id: string;
  name: string;
  color: string;
  value: number;
  workloadId?: string;
};

const getOomStepSeconds = (from: string, to: string): number => {
  const diffInSeconds = dayjs(to).diff(dayjs(from), 'seconds');

  if (diffInSeconds <= SECONDS_IN_DAY * 10) {
    return 300;
  }

  if (diffInSeconds > SECONDS_IN_DAY * 31) {
    return SECONDS_IN_DAY;
  }

  return SECONDS_IN_HOUR;
};

type WorkloadAggregate = {
  id: string;
  name: string;
  workloadId?: string;
  eventCount: number;
};

type InventoryOomKillsData = {
  chartData: OOMKillChartItem[];
  topItems: InventoryOomKillsTopItem[];
  totals: OOMKillTotals;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  timeZone: string;
  rangeSelection: OomKillBucketQueryRange;
};

export const useInventoryOomKillsData = (): InventoryOomKillsData => {
  const { cluster } = useCluster();
  const { getRange, key, timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const range = getRange();
  const stepSeconds = getOomStepSeconds(range.from, range.to);

  const rangeSelection = useMemo<OomKillBucketQueryRange>(
    () => ({
      getRange,
      key,
      stepSeconds,
    }),
    [getRange, key, stepSeconds]
  );

  const { chartData, totals, isLoading, isError, refetch } =
    useOomKillEventMetricsChartData({
      rangeSelection,
      enabled: !!cluster.id,
    });

  // Same query key as useOomKillEventMetricsChartData — served from cache.
  const {
    data: series,
    isFetching,
    error,
  } = useWorkloadEventMetricsQuery({
    clusterId: cluster.id,
    startTime: range.from,
    endTime: range.to,
    eventTypes: ['OOMKilled'],
    stepSeconds,
    groupByManagedBy: true,
    key,
    enabled: !!cluster.id,
  });

  const topItems = useMemo((): InventoryOomKillsTopItem[] => {
    const oomSeries = series?.filter((s) => s.eventType === 'OOMKilled') ?? [];
    const byWorkload = new Map<string, WorkloadAggregate>();

    for (const seriesItem of oomSeries) {
      for (const item of seriesItem.items ?? []) {
        for (const workload of item.byWorkload ?? []) {
          const workloadId = (workload as { workloadId?: string | null })
            .workloadId;
          const keyId =
            workloadId ||
            `${workload.namespace}/${workload.workloadType}/${workload.workloadName}`;
          const existing = byWorkload.get(keyId);
          const eventCount = parseInt(workload.eventCount, 10) || 0;

          byWorkload.set(keyId, {
            id: keyId,
            name: workload.workloadName,
            workloadId: workloadId ?? undefined,
            eventCount: (existing?.eventCount ?? 0) + eventCount,
          });
        }
      }
    }

    if (byWorkload.size > 0) {
      return [...byWorkload.values()]
        .sort((a, b) => b.eventCount - a.eventCount)
        .slice(0, TOP_WORKLOADS_LIMIT)
        .map((item, index) => ({
          id: item.id,
          name: item.name,
          color: WORKLOAD_COLORS[index % WORKLOAD_COLORS.length],
          value: item.eventCount,
          workloadId: item.workloadId,
        }));
    }

    const fallback: InventoryOomKillsTopItem[] = [];
    if (totals.managed > 0) {
      fallback.push({
        id: 'managed',
        name: 'Managed',
        color: MANAGED_COLOR,
        value: totals.managed,
      });
    }
    if (totals.unmanaged > 0) {
      fallback.push({
        id: 'unmanaged',
        name: 'Unmanaged',
        color: UNMANAGED_COLOR,
        value: totals.unmanaged,
      });
    }

    return fallback.sort((a, b) => b.value - a.value);
  }, [series, totals.managed, totals.unmanaged]);

  return {
    chartData,
    topItems,
    totals,
    isLoading,
    isFetching,
    error: error ?? (isError ? new Error('Failed to load OOM kills') : null),
    refetch,
    timeZone,
    rangeSelection,
  };
};
